import { Checkbox, Form, InputNumber, Modal, Space, Spin } from 'antd';
import { CheckboxValueType } from 'antd/lib/checkbox/Group';
import dayjs, { Dayjs } from 'dayjs';
import _ from 'lodash';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dispatch } from 'redux';
import IApplicationProperty from '~Api/Application/IApplicationProperty';
import ILoan from '~Api/Loan/ILoan';
import { applicationPropertiesListAction } from '~Applications/actions';
import { applicationPropertiesSelector } from '~Applications/selectors';
import { loanPrincipalReductionAction } from '~Loans/actions';
import { IGlobalState } from '~reducer';
import DatePicker from '~UI/DatePicker';
import TimePicker from '~UI/TimePicker';
import { IDictionary } from '~utilities/IDictionary';

interface IPrincipalReductionErrors {
    amount?: string;
    properties?: string;
    transactionDateTime?: string;
}

export default function PrincipalReductionModal(props: {
    loan: ILoan;
    isOpen: boolean;
    onCancel: () => void;
}): ReactElement {
    const { isOpen, loan, onCancel } = props;

    const [ amount, setAmount ] = useState<number>(null);
    const [ errors, setErrors ] = useState<IPrincipalReductionErrors>({});
    const [ selectedPropertyUuids, setSelectedPropertyUuids ] = useState<string[]>([]);
    const [ transactionDateTime, setTransactionDateTime ] = useState<string>(null);

    const applicationProperties: IDictionary<IApplicationProperty> = useSelector((state: IGlobalState) => applicationPropertiesSelector(state, loan.applicationUuid));

    const dispatch: Dispatch = useDispatch();

    useEffect(() => {
        if (!applicationProperties) {
            dispatch(applicationPropertiesListAction(loan.applicationUuid));
        }
    }, [
        dispatch,
        loan.applicationUuid,
    ]);

    const validateAmount: () => boolean = useCallback(() => {
        let error: string = null;

        if (!amount) {
            error = 'Amount is required';
        } else if (amount <= 0) {
            error = 'Amount must be greater than 0';
        } else if (amount >= loan.amountRemaining) {
            error = 'Amount must be less than the loan\'s remaining amount';
        }

        setErrors((prevErrors: IPrincipalReductionErrors) => ({
            ...prevErrors,
            amount: error,
        }));

        return !error;
    }, [
        amount,
        loan.amountRemaining,
    ]);

    const validateProperties: () => boolean = useCallback(() => {
        let error: string = null;

        if (activeApplicationProperties.length === selectedPropertyUuids.length) {
            error = 'Loan should have at least one property';
        }

        setErrors((prevErrors: IPrincipalReductionErrors) => ({
            ...prevErrors,
            properties: error,
        }));

        return !error;
    }, [
        selectedPropertyUuids,
    ]);

    const validateTransactionDateTime: () => boolean = useCallback(() => {
        let error: string = null;

        if (!transactionDateTime) {
            error = 'Transaction date and time is required';
        } else if (dayjs(transactionDateTime).isAfter(dayjs())) {
            error = 'Transaction date and time cannot be in the future';
        }

        setErrors((prevErrors: IPrincipalReductionErrors) => ({
            ...prevErrors,
            transactionDateTime: error,
        }));

        return !error;
    }, [
        transactionDateTime,
    ]);

    const onClickOk: () => void = useCallback(() => {
        let valid: boolean = true;

        valid = validateAmount() && valid;
        valid = validateProperties() && valid;
        valid = validateTransactionDateTime() && valid;

        if (!valid) {
            return;
        }

        dispatch(loanPrincipalReductionAction(loan.uuid, amount, dayjs(transactionDateTime).format('YYYY-MM-DDTHH:mm:ssZ'), selectedPropertyUuids));
        onCancel();
    }, [
        amount,
        dispatch,
        loan.uuid,
        onCancel,
        selectedPropertyUuids,
        transactionDateTime,
        validateAmount,
        validateProperties,
        validateTransactionDateTime,
    ]);

    const onChangeDate: (date: Dayjs) => void = useCallback((date: Dayjs) => {
        setTransactionDateTime(dayjs(`${date.format('YYYY-MM-DD')} ${dayjs(transactionDateTime || undefined).format('HH:mm')}`).format());
    }, [ transactionDateTime ]);

    const onChangeTime: (time: Dayjs) => void = useCallback((time: Dayjs) => {
        setTransactionDateTime(dayjs(`${dayjs(transactionDateTime || undefined).format('YYYY-MM-DD')} ${time.format('HH:mm')}`).format());
    }, [ transactionDateTime ]);

    const onChangeAmount: (value: number) => void = useCallback((value: number) => {
        setAmount(value);
    }, []);

    const onChangeSelectedProperties: (checkedValue: CheckboxValueType[]) => void = useCallback((checkedValue: CheckboxValueType[]) => {
        setSelectedPropertyUuids(checkedValue as string[]);
    }, []);

    const transactionDateTimeDayjs: Dayjs = transactionDateTime ? dayjs(transactionDateTime) : null;

    if (!applicationProperties) {
        return (
            <Modal
                destroyOnClose={true}
                okText='Record'
                okType='danger'
                onCancel={onCancel}
                onOk={onClickOk}
                okButtonProps={{ disabled: true }}
                open={isOpen}
                title='Principal Reduction'
                wrapClassName='loan-principal-reduction-modal'
            >
                <Spin />
            </Modal>
        );
    }

    const activeApplicationProperties: IApplicationProperty[] = _.filter(applicationProperties, (applicationProperty: IApplicationProperty) => !applicationProperty.dealProperty.dischargeTime);

    const releasePropertiesOptions: JSX.Element[] = activeApplicationProperties.map((property: IApplicationProperty) => (
        <Checkbox key={property.dealProperty.uuid} value={property.dealProperty.uuid}>
            {property.dealProperty.formattedAddress}
        </Checkbox>
    ));

    const releasePropertiesBlock: JSX.Element = activeApplicationProperties.length > 1 && (
        <Form.Item className='properties' label='Properties to Release' validateStatus={errors.properties && 'error'} help={errors.properties}>
            <Checkbox.Group value={selectedPropertyUuids} onChange={onChangeSelectedProperties}>
                <Space direction='vertical'>
                    {releasePropertiesOptions}
                </Space>
            </Checkbox.Group>
        </Form.Item>
    );

    return (
        <Modal
            destroyOnClose={true}
            okText='Record'
            okType='danger'
            onCancel={onCancel}
            onOk={onClickOk}
            open={isOpen}
            title='Principal Reduction'
            width={650}
            wrapClassName='loan-principal-reduction-modal'
        >
            <Form.Item className='transaction-date' label='Transaction Date' validateStatus={errors.transactionDateTime && 'error'} help={errors.transactionDateTime}>
                <Space>
                    <DatePicker
                        className='transaction-pick-date'
                        onChange={onChangeDate}
                        value={transactionDateTimeDayjs}
                        format='DD/MM/YYYY'
                    />
                    <TimePicker
                        className='transaction-pick-time'
                        onChange={onChangeTime}
                        value={transactionDateTimeDayjs}
                        format='H:mm'
                    />
                </Space>
            </Form.Item>
            <Form.Item className='amount' label='Amount' validateStatus={errors.amount && 'error'} help={errors.amount}>
                <InputNumber addonBefore='$' min={0} onBlur={validateAmount} onChange={onChangeAmount} type='number' value={amount} />
            </Form.Item>
            {releasePropertiesBlock}
        </Modal>
    );
}
